import { baseApi } from "../baseApi";
import { pick } from "../util";
import type { Payment, CreatePaymentInput, UpdatePaymentInput } from "../types";

const PAYMENT_FIELDS = `
  id
  organizationId
  paymentNumber
  invoiceId
  customerId
  paymentDate
  amount
  method
  reference
  status
  notes
  createdAt
  updatedAt
`;

export const paymentApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getPayments: builder.query<Payment[], string | void>({
      query: (organizationId) => ({
        body: {
          query: `
            query Payments($organizationId: String) {
              payments(organizationId: $organizationId) {
                ${PAYMENT_FIELDS}
              }
            }
          `,
          variables: { organizationId: organizationId ?? null },
        },
      }),

      transformResponse: (res: unknown) => pick<Payment[]>(res, "payments"),

      providesTags: (result) =>
        result
          ? [
              ...result.map((p) => ({ type: "Payment" as const, id: p.id })),
              { type: "Payment" as const, id: "LIST" },
            ]
          : [{ type: "Payment" as const, id: "LIST" }],
    }),

    getPayment: builder.query<Payment, string>({
      query: (id) => ({
        body: {
          query: `
            query Payment($id: String!) {
              payment(id: $id) {
                ${PAYMENT_FIELDS}
              }
            }
          `,
          variables: { id },
        },
      }),

      transformResponse: (res: unknown) => pick<Payment>(res, "payment"),

      providesTags: (_result, _err, id) => [{ type: "Payment", id }],
    }),

    createPayment: builder.mutation<Payment, CreatePaymentInput>({
      query: (input) => ({
        body: {
          query: `
            mutation CreatePayment($input: CreatePaymentInput!) {
              createPayment(input: $input) {
                ${PAYMENT_FIELDS}
              }
            }
          `,
          variables: { input },
        },
      }),

      transformResponse: (res: unknown) => pick<Payment>(res, "createPayment"),

      invalidatesTags: [{ type: "Payment", id: "LIST" }],
    }),

    updatePayment: builder.mutation<
      Payment,
      { id: string; input: UpdatePaymentInput }
    >({
      query: ({ id, input }) => ({
        body: {
          query: `
            mutation UpdatePayment($id: String!, $input: UpdatePaymentInput!) {
              updatePayment(id: $id, input: $input) {
                ${PAYMENT_FIELDS}
              }
            }
          `,
          variables: { id, input },
        },
      }),

      transformResponse: (res: unknown) => pick<Payment>(res, "updatePayment"),

      invalidatesTags: (_result, _err, { id }) => [
        { type: "Payment", id },
        { type: "Payment", id: "LIST" },
      ],
    }),

    deletePayment: builder.mutation<boolean, string>({
      query: (id) => ({
        body: {
          query: `
            mutation DeletePayment($id: String!) {
              deletePayment(id: $id)
            }
          `,
          variables: { id },
        },
      }),

      transformResponse: (res: unknown) => pick<boolean>(res, "deletePayment"),

      invalidatesTags: (_result, _err, id) => [
        { type: "Payment", id },
        { type: "Payment", id: "LIST" },
      ],
    }),
  }),
});

export const {
  useGetPaymentsQuery,
  useLazyGetPaymentsQuery,
  useGetPaymentQuery,
  useLazyGetPaymentQuery,
  useCreatePaymentMutation,
  useUpdatePaymentMutation,
  useDeletePaymentMutation,
} = paymentApi;
